import { cn } from "../lib/cn";

/**
 * The b-r-a-i-n letters with the two accent slots that trade places.
 *
 * The resolved offsets live on the elements, so the mark reads "Brian" with no
 * animation running at all (animate off, or reduced motion). While the shared
 * bds-swap-a/i keyframes play they override those offsets, and the word
 * cycles brain -> Brian -> brain.
 *
 * The swap moves the letters with left/top rather than a transform: an infinite
 * transform animation keeps each letter on its own compositing layer, and layer
 * text resamples through the tag's rotation, so the two moving letters rendered
 * soft next to their static neighbours. Offsets stay off the compositor.
 *
 * Each cell is as wide as its letter's advance width in the face being used, so
 * both spellings sit at that face's natural spacing. Because the cells differ,
 * each letter travels the width of the cell it lands in, not its own: the a
 * moves right by the i cell and the i left by the a cell. That distance is
 * handed to the keyframes as --swap-x and duplicated in the resolved offset,
 * so the static and animated ends of the swap agree.
 */
function SwapLetters({
  aWidth,
  iWidth,
  animate,
}: {
  aWidth: string;
  iWidth: string;
  animate: boolean;
}) {
  const slot = "relative inline-block text-center text-accent";

  return (
    <>
      B<span aria-hidden="true">r</span>
      <span
        aria-hidden="true"
        style={
          {
            width: aWidth,
            "--swap-x": iWidth,
            left: iWidth,
          } as React.CSSProperties
        }
        className={cn(
          slot,
          animate &&
            "[animation:bds-swap-a_8s_var(--ease-paper)_infinite] motion-reduce:animate-none",
        )}
      >
        a
      </span>
      <span
        aria-hidden="true"
        style={
          {
            width: iWidth,
            "--swap-x": aWidth,
            left: `-${aWidth}`,
          } as React.CSSProperties
        }
        className={cn(
          slot,
          animate &&
            "[animation:bds-swap-i_8s_var(--ease-paper)_infinite] motion-reduce:animate-none",
        )}
      >
        i
      </span>
      <span aria-hidden="true">n</span>
    </>
  );
}

export interface WordmarkProps {
  /** Cycle the swap. Off holds the resolved "Brian". Defaults to on. */
  animate?: boolean;
  className?: string;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * The signature: the letters a and i swap so brain becomes Brian and back. The
 * clinic's digital brain, and the ai in it, in one mark. Size it from the outside
 * with a `text-*` class; everything inside is em-based. Color is inherited, so it
 * works on paper or on the desk.
 */
export function Wordmark({ animate = true, className, ref }: WordmarkProps) {
  return (
    <span
      ref={ref}
      role="img"
      aria-label="Brian"
      className={cn(
        "font-display font-medium tracking-[-0.05em] whitespace-nowrap",
        className,
      )}
    >
      {/* Space Grotesk sets a and i on the same 0.58em cell. */}
      <SwapLetters aWidth="0.58em" iWidth="0.58em" animate={animate} />
    </span>
  );
}

export interface NameTagProps {
  /** Defaults to "Brian", which renders with the letter swap. Other names render plain. */
  name?: string;
  subtitle?: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The HELLO my name is tag: the brand moment, and how Brian introduces himself.
 * A stuck-on paper object with the name handwritten in marker, sitting square.
 * Fully em-based, so `font-size` on the root scales the whole tag.
 */
export function NameTag({
  name = "Brian",
  subtitle = "my name is",
  className,
  ref,
}: NameTagProps) {
  const isBrian = name === "Brian";

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-tag bg-card border border-rule shadow-lift-lg overflow-hidden",
        className,
      )}
    >
      <div className="bg-accent text-center py-[0.5em] px-[0.75em]">
        <span className="block font-display font-bold uppercase tracking-[0.16em] text-ink-inverse text-[1.55em] leading-none">
          Hello
        </span>
      </div>
      <div className="bg-card text-center px-[1.1em] pt-[0.85em] pb-[1.15em]">
        <p className="italic text-ink-body text-[0.68em] tracking-[0.02em]">
          {subtitle}
        </p>
        <div className="h-px bg-rule" />
        <p
          role="img"
          aria-label={name}
          className="font-hand font-bold text-accent text-[2.5em] leading-[1.15] whitespace-nowrap"
        >
          {/* Caveat bold: the a runs 0.45em, the i only 0.2em. */}
          {isBrian ? (
            <SwapLetters aWidth="0.45em" iWidth="0.2em" animate />
          ) : (
            name
          )}
        </p>
      </div>
    </div>
  );
}
